import { observer } from 'mobx-react';
import { FC } from 'react';
import classNames from 'classnames';
import { localeStore } from '~/mobx/localesStore';
import Input from '~/view/components/Controls/InputComponent/Input';
import { CurrencyPicker } from '~/view/components/Controls/Pickers/CurrencyPicker/CurrencyPicker';
import Text, { TEXT } from '~/view/components/Text/Text';
import { TOOLTIP_DURATION, TOOLTIP_POSITION, TOOLTIP_TYPE } from '~/view/components/Controls/CustomTooltip/CustomTooltip';
import Switcher from '~/view/components/Controls/Switcher/Switcer';
import Button from '~/view/components/Controls/ButtonComponent/Button';
import { useWalletSettings } from '~/view/screens/MarketplaceScreen/containers/MarketplaceFeatureEdit/WalletAddon/hooks/useWalletSettings';
import './style.scss';
import marketplaceStore from '~/view/screens/MarketplaceScreen/store/marketplaceStore';
import { booleanToNumber } from '~/utils/helpers';

const WalletAddonSettings: FC = observer((): JSX.Element | null => {
  const feature = marketplaceStore.selectedFeature || {
    walletCurrencyEn: '',
    walletCurrencyAr: '',
    walletCurrencyId: '',
    walletCurrency: '',
    isWalletUsageLimit: 0,
    walletUsageLimit: '',
    status: false,
  };

  const {
    currencyName,
    currencyCode,
    isLimit,
    limitValue,
    validationErrors,
    status,
    loading,
    handleCurrencyNameChange,
    handleCurrencySelect,
    handleLimitToggle,
    handleWalletUsageLimitChange,
    handleStatusChange,
    handleSaveData,
  } = useWalletSettings({
    walletCurrencyEn: feature.walletCurrencyEn,
    walletCurrencyAr: feature.walletCurrencyAr,
    walletCurrencyId: feature.walletCurrencyId,
    walletCurrency: feature.walletCurrency,
    isWalletUsageLimit: feature.isWalletUsageLimit,
    walletUsageLimit: feature.walletUsageLimit,
    enabled: feature.status,
  });

  return (
    <div className="wallet-addon d-flex flex-column mt-5">
      <div>
        <div className="d-flex align-items-center mb-4">
          <Switcher status={booleanToNumber(status)} onChange={handleStatusChange} />
          <Text type={TEXT.SWITCHER_LABEL} text={localeStore.t('enable')} />
        </div>
        <Text type={TEXT.SECTION_HEADER} text={localeStore.t('wallet_currency_name')} />
        <div className="d-flex flex-row align-items-center wallet-addon__field mt-4">
          <p className="family-medium text-size-18 flex-1">{localeStore.t('english')}</p>
          <Input
            value={currencyName.walletCurrencyEn}
            placeholder={localeStore.t('Value')}
            onChangeValue={handleCurrencyNameChange('walletCurrencyEn')}
          />
        </div>
        <div className="d-flex flex-row align-items-center mt-3 wallet-addon__field">
          <p className="family-medium text-size-18 flex-1">{localeStore.t('Arabic')}</p>
          <Input
            value={currencyName.walletCurrencyAr}
            placeholder={localeStore.t('Value')}
            onChangeValue={handleCurrencyNameChange('walletCurrencyAr')}
          />
        </div>
        <div className="d-flex flex-row align-items-center mt-7 wallet-addon__field">
          <Text
            type={TEXT.CUSTOM}
            text="currency"
            className="family-medium text-size-18"
            wrapperClassName="flex-1"
            tooltip={{
              message: 'wallet_default_currency',
              position: TOOLTIP_POSITION.FIXED,
              duration: TOOLTIP_DURATION.EXTRA_LARGE,
              type: TOOLTIP_TYPE.INFO,
            }}
          />
          <CurrencyPicker
            id={currencyCode.walletCurrencyId}
            code={currencyCode.walletCurrency}
            visibleOptionsNum={3}
            onSelect={handleCurrencySelect}
          />
        </div>
        <div className="d-flex flex-column mt-6">
          <Text type={TEXT.SECTION_HEADER} text="marketplace_wallet_usage_limit_block_hdr" />

          <div className="d-flex align-items-center mt-4">
            <Switcher status={isLimit} onChange={handleLimitToggle} />
            <Text type={TEXT.SWITCHER_LABEL} text="marketplace_wallet_usage_limit_toggle" className="ml-5" />
          </div>

          {isLimit ? (
            <div className="mt-4">
              <Text
                type={TEXT.BLOCK_HEADER}
                text="marketplace_wallet_usage_limit_percentage_field_label"
                tooltip={{ message: 'marketplace_wallet_usage_limit_percentage_field_label_infotip' }}
              />
              <Input
                value={limitValue}
                onChangeValue={handleWalletUsageLimitChange}
                placeholder={localeStore.t('Value')}
                contentClassName={classNames('wallet-addon__limit-input mt-2', { 'border-red': validationErrors.walletUsageLimit })}
              />
              {validationErrors.walletUsageLimit ? <Text type={TEXT.ERROR} text={validationErrors.walletUsageLimit} className="mt-2" /> : null}
            </div>
          ) : null}
        </div>

        <Button title={localeStore.t('txt_save')} onClick={handleSaveData} size="large" loading={loading} className="mt-8" />
      </div>
    </div>
  );
});

export default WalletAddonSettings;
